import React, { useCallback, useMemo, useState } from 'react'
import {
  TextField,
  TableCell,
  TableRow,
  Typography,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { Theme } from '@mui/material/styles'
import { BidTableRow } from './bids-table'
import { clamp, formatPercentage, roundToDecimals } from '../../shared/formats'
import { PaymentRow, PaymentRowType, ScheduleType } from '../../shared/types'
import PaymentScheduleTable from '../create-proposal/payment-schedule-table'
import PaymentScheduleRow from '../create-proposal/payment-schedule-row'
import {
  normalizePaymentRowToPercentage,
  paymentDateComparator,
} from '../create-proposal/conversion-utils'
import { useFeatureFlags } from '../../shared/use-feature-flags'
import { FeatureFlagName } from '../../../api/feature-flags-api'

export interface EditableBidRow extends BidTableRow {
  negotiatedRate: number | null
  treasuryRate: number | null
  amount: string // Stored as formatted string like "1,000,000"
  scenarioId: string
  issuerTenantId: string
  wamYears: number | null
  paymentSchedule: string
  editablePaymentSchedule: PaymentRow[]
  originalPaymentSchedule: PaymentRow[]
}

export interface EditableBidRowComponentProps {
  bid: EditableBidRow
  expandedNotes: Set<string>
  expandedPaymentSchedules: Set<string>
  toggleNotes: (bidId: string) => void
  togglePaymentSchedule: (bidId: string) => void
  updateBidField: (
    bidId: string,
    field: keyof EditableBidRow,
    value: number | string | null | PaymentRow[]
  ) => void
  parseFormattedNumber: (value: string) => number
  theme: Theme
  scheduleType: ScheduleType
  depositAmount: number
}

export const EditableBidRowComponent: React.FC<
  EditableBidRowComponentProps
> = ({
  bid,
  expandedNotes,
  expandedPaymentSchedules,
  toggleNotes,
  togglePaymentSchedule,
  updateBidField,
  parseFormattedNumber,
  theme,
  scheduleType,
  depositAmount,
}) => {
  const isPaymentScheduleExpanded = expandedPaymentSchedules.has(bid.bidId)

  const { flags } = useFeatureFlags()
  const enableDollarAmount =
    flags[FeatureFlagName.ENABLE_PARTIAL_MATURITIES_BY_DOLLAR_AMOUNT]

  const [hoverDeleteRow, setHoverDeleteRow] = useState<number | null>(null)
  const [touched, setTouched] = useState(
    bid.editablePaymentSchedule.map(() => ({
      payoutPercentage: false,
      paymentDate: false,
    }))
  )

  const totalPercentage = useMemo(
    () =>
      roundToDecimals(
        bid.editablePaymentSchedule.reduce(
          (sum, row) =>
            sum +
            normalizePaymentRowToPercentage(
              row,
              enableDollarAmount,
              depositAmount
            ),
          0
        ),
        3
      ),
    [bid.editablePaymentSchedule, enableDollarAmount, depositAmount]
  )

  const handleAddPaymentRow = useCallback(() => {
    const updated = [
      ...bid.editablePaymentSchedule,
      { payoutPercentage: 0, payoutAmount: 0, paymentDate: null },
    ]
    updateBidField(bid.bidId, 'editablePaymentSchedule', updated)
    setTouched((prev) => [
      ...prev,
      { payoutPercentage: false, paymentDate: false },
    ])
  }, [bid.editablePaymentSchedule, bid.bidId, updateBidField])

  const handleDeletePaymentRow = useCallback(
    (index: number) => {
      const updated = [...bid.editablePaymentSchedule]
      updated.splice(index, 1)
      updateBidField(bid.bidId, 'editablePaymentSchedule', updated)
    },
    [bid.editablePaymentSchedule, bid.bidId, updateBidField]
  )

  const handlePayoutPercentageChange = useCallback(
    (index: number, rawValue: string) => {
      const inputValue = rawValue.trim()
      const currentRow = bid.editablePaymentSchedule[index]
      const isPercentageMode =
        (currentRow.inputType ?? PaymentRowType.PERCENTAGE) ===
        PaymentRowType.PERCENTAGE

      let value = 0
      if (inputValue !== '') {
        const numericValue = Number.parseFloat(inputValue)
        if (!Number.isNaN(numericValue)) {
          value = isPercentageMode
            ? clamp(numericValue, 0, 100)
            : Math.max(0, numericValue)
        }
      }

      const updated = [...bid.editablePaymentSchedule]
      updated[index] = {
        ...updated[index],
        payoutAmount: enableDollarAmount ? value : 0,
        payoutPercentage: enableDollarAmount ? 0 : value,
      }
      updateBidField(bid.bidId, 'editablePaymentSchedule', updated)

      const updatedTouched = [...touched]
      updatedTouched[index] = {
        ...updatedTouched[index],
        payoutPercentage: true,
      }
      setTouched(updatedTouched)
    },
    [
      bid.editablePaymentSchedule,
      bid.bidId,
      updateBidField,
      touched,
      enableDollarAmount,
    ]
  )

  const handlePaymentDateChange = useCallback(
    (index: number, date: Date | string | null) => {
      const updated = [...bid.editablePaymentSchedule]
      updated[index] = { ...updated[index], paymentDate: date }
      updateBidField(bid.bidId, 'editablePaymentSchedule', updated)

      const updatedTouched = [...touched]
      updatedTouched[index] = { ...updatedTouched[index], paymentDate: true }
      setTouched(updatedTouched)
    },
    [bid.editablePaymentSchedule, bid.bidId, updateBidField, touched]
  )

  const handleSortByDate = useCallback(
    (newSortOrder: 'asc' | 'desc') => {
      const sorted = [...bid.editablePaymentSchedule].sort((a, b) => {
        const cmp = paymentDateComparator(a, b)
        return newSortOrder === 'asc' ? cmp : -cmp
      })
      updateBidField(bid.bidId, 'editablePaymentSchedule', sorted)
    },
    [bid.editablePaymentSchedule, bid.bidId, updateBidField]
  )

  const handleAmountChange = useCallback(
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (e.type === 'blur') {
        const value = e.target.value
        if (value !== '' && !isNaN(parseFloat(value))) {
          const parts = value.split('.')
          const integerPart = parseInt(parts[0]).toLocaleString('en-US')
          const formattedValue =
            parts[1] !== undefined ? `${integerPart}.${parts[1]}` : integerPart
          updateBidField(bid.bidId, 'amount', formattedValue)
        }
      } else {
        let value = e.target.value.replace(/[^0-9.]/g, '')

        // Prevent multiple decimal points
        const decimalCount = (value.match(/\./g) ?? []).length
        if (decimalCount > 1) {
          value = value.substring(0, value.lastIndexOf('.'))
        }

        // Limit to 2 decimal places
        const decimalIndex = value.indexOf('.')
        if (decimalIndex !== -1 && value.length - decimalIndex > 3) {
          value = value.substring(0, decimalIndex + 3)
        }

        // Guard against non-numeric values (e.g. bare ".")
        if (value !== '' && isNaN(parseFloat(value))) {
          value = ''
        }

        updateBidField(bid.bidId, 'amount', value)
      }
    },
    [bid.bidId, updateBidField]
  )

  const handleResetPaymentSchedule = useCallback(() => {
    updateBidField(
      bid.bidId,
      'editablePaymentSchedule',
      bid.originalPaymentSchedule.map((row) => ({ ...row }))
    )
    setTouched(
      bid.originalPaymentSchedule.map(() => ({
        payoutPercentage: false,
        paymentDate: false,
      }))
    )
  }, [bid.bidId, bid.originalPaymentSchedule, updateBidField])

  const paymentScheduleRows = useMemo(
    () =>
      bid.editablePaymentSchedule.map((row, rowIndex) => (
        <PaymentScheduleRow
          // eslint-disable-next-line react-x/no-array-index-key
          key={`ps-${bid.bidId}-${rowIndex}`}
          row={row}
          rowIndex={rowIndex}
          scheduleType={scheduleType}
          totalRows={bid.editablePaymentSchedule.length}
          touched={
            touched[rowIndex] ?? {
              payoutPercentage: false,
              paymentDate: false,
            }
          }
          hoverDeleteRow={hoverDeleteRow}
          canDelete={bid.editablePaymentSchedule.length > 1}
          onPayoutPercentageChange={handlePayoutPercentageChange}
          onPaymentDateChange={handlePaymentDateChange}
          onDeleteRow={handleDeletePaymentRow}
          onMouseEnterDelete={setHoverDeleteRow}
          onMouseLeaveDelete={() => setHoverDeleteRow(null)}
        />
      )),
    [
      bid.editablePaymentSchedule,
      bid.bidId,
      scheduleType,
      touched,
      hoverDeleteRow,
      handlePayoutPercentageChange,
      handlePaymentDateChange,
      handleDeletePaymentRow,
    ]
  )

  return (
    <React.Fragment key={`finalize--editable-bid--${bid.bidId}`}>
      <TableRow>
        <TableCell>{bid.issuerName}</TableCell>
        <TableCell align="right">{formatPercentage(bid.rate)}</TableCell>
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={
              bid.treasuryRate === null || bid.treasuryRate === 0
                ? ''
                : bid.treasuryRate
            }
            onChange={(e) => {
              const value =
                e.target.value === '' ? null : parseFloat(e.target.value)
              updateBidField(bid.bidId, 'treasuryRate', value)
            }}
            error={bid.treasuryRate === null || bid.treasuryRate < 0}
            required
            sx={{ width: '120px' }}
            slotProps={{
              htmlInput: {
                step: '0.01',
                min: 0,
              },
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            type="number"
            value={
              bid.negotiatedRate === null || bid.negotiatedRate === 0
                ? ''
                : bid.negotiatedRate
            }
            onChange={(e) => {
              const value =
                e.target.value === '' ? null : parseFloat(e.target.value)
              updateBidField(bid.bidId, 'negotiatedRate', value)
            }}
            error={bid.negotiatedRate == null || bid.negotiatedRate <= 0}
            required
            sx={{ width: '120px' }}
            slotProps={{
              htmlInput: {
                step: '0.01',
                min: 0.01,
              },
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
            }}
          />
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            type="text"
            value={bid.amount}
            onChange={handleAmountChange}
            onBlur={handleAmountChange}
            error={bid.amount === '' || parseFormattedNumber(bid.amount) <= 0}
            required
            sx={{ width: '150px' }}
            slotProps={{
              htmlInput: {
                inputMode: 'numeric',
              },
              input: {
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              },
            }}
          />
        </TableCell>
        <TableCell align="right">
          <TextField
            size="small"
            type="number"
            value={bid.wamYears ?? ''}
            onChange={(e) => {
              const value =
                e.target.value === '' ? null : parseFloat(e.target.value)
              updateBidField(bid.bidId, 'wamYears', value)
            }}
            sx={{ width: '90px' }}
            slotProps={{
              htmlInput: {
                step: '0.1',
                min: 0,
              },
            }}
          />
        </TableCell>
        <TableCell>
          {bid.notes != null && bid.notes !== '' && (
            <Button
              size="small"
              variant="text"
              onClick={() => toggleNotes(bid.bidId)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {expandedNotes.has(bid.bidId) ? 'Close Notes' : 'View Notes'}
            </Button>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', flex: 1 }}>
              {bid.paymentSchedule}
            </Typography>
            <Tooltip
              title={
                isPaymentScheduleExpanded
                  ? 'Close Payment Schedule'
                  : 'Edit Payment Schedule'
              }
            >
              <IconButton
                size="small"
                onClick={() => togglePaymentSchedule(bid.bidId)}
                sx={{
                  color: isPaymentScheduleExpanded
                    ? theme.palette.primary.main
                    : theme.palette.action.active,
                  backgroundColor: isPaymentScheduleExpanded
                    ? theme.palette.primary.main + '14'
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: isPaymentScheduleExpanded
                      ? theme.palette.primary.main + '28'
                      : theme.palette.action.hover,
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
      {expandedNotes.has(bid.bidId) && (
        <TableRow>
          <TableCell
            colSpan={8}
            sx={{ backgroundColor: theme.palette.grey[100] }}
          >
            <Typography variant="subtitle2" color="primary">
              Notes
            </Typography>
            <Typography variant="body2">{bid.notes ?? '---'}</Typography>
          </TableCell>
        </TableRow>
      )}
      {isPaymentScheduleExpanded && (
        <TableRow>
          <TableCell
            colSpan={8}
            sx={{ backgroundColor: theme.palette.grey[100], p: 2 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" color="primary">
                  Edit Payment Schedule
                </Typography>
              </Box>
              <Tooltip title="Reset to original">
                <IconButton size="small" onClick={handleResetPaymentSchedule}>
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <PaymentScheduleTable
              scheduleType={scheduleType}
              totalPercentage={totalPercentage}
              onAddRow={handleAddPaymentRow}
              onSort={handleSortByDate}
              hideHeader
            >
              {paymentScheduleRows}
            </PaymentScheduleTable>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  )
}
